import React, { useContext, Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TitleBreadcrumb } from "../../components/nav/TitleBreadcrumb";
import { UserMenu } from "../../components/nav/UserMenu";
import { RoleSwitchButton } from "../../components/nav/RoleSwitchButton";
import { useTranslation } from "react-i18next";
import { useCan } from "../../authz/useCan";
import { useActiveRole } from "../../utils/activeRole";
import { useStartPageAccess } from "../../utils/useStartPageAccess";
import { pickStartPagePreference, resolveStaffStartRoute } from "../../utils/startPagePreference";
import { AuthContext } from "../../AuthProvider";
import { Menu, Transition } from "@headlessui/react";
import { FiMenu, FiUser, FiLogOut, FiArrowRight, FiRefreshCcw } from "react-icons/fi";
import config from "../../config";

export default function ParentNavbar({ backendOnline }) {
  const { authenticated, keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isLocalhost = useMemo(() => {
    try {
      if (typeof window === "undefined") return false;
      const host = window.location?.hostname || "";
      return host === "localhost" || host === "127.0.0.1" || host === "::1";
    } catch {
      return false;
    }
  }, []);

  // “heeft admin-rechten?” (rol/policy check)
  const startPageAccess = useStartPageAccess();
  const canEnterStaffPortal = startPageAccess.canEnterStaffPortal;
  const staffStartRoute =
    resolveStaffStartRoute({
      ...startPageAccess,
      preference: pickStartPagePreference(null, keycloak?.tokenParsed),
    }) || "/dashboard";
  const canManageOwnProfile = useCan("profile.self", "manage");
  const canOpenProfile = canEnterStaffPortal || canManageOwnProfile;
  const { setActiveRole } = useActiveRole();

  const hasAnyStaffRole = useMemo(() => {
    if (!keycloak?.tokenParsed) return false;
    const realm = keycloak.tokenParsed?.realm_access?.roles ?? [];
    const clients = Object.values(keycloak.tokenParsed?.resource_access ?? {}).flatMap(
      (ra) => ra?.roles ?? []
    );
    const roles = [...realm, ...clients].map((r) => String(r).toLowerCase());
    const staffHints = ["admin", "staff", "teacher", "beheer"];
    return staffHints.some((hint) => roles.some((role) => role.includes(hint)));
  }, [keycloak?.tokenParsed]);

  // Toon “Switch to Admin” alleen als user aantoonbaar staff-toegang heeft of policy geeft toestemming
  const [familySwitchAvailable, setFamilySwitchAvailable] = useState(false);
  const showSwitchToAdmin = authenticated && (canEnterStaffPortal || hasAnyStaffRole);
  const isPortal = location?.pathname?.startsWith("/portal");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isPortal) {
      setFamilySwitchAvailable(false);
      return;
    }
    const updateFromStorage = () => {
      const raw = window.sessionStorage?.getItem("parentPortalFamiliesCount");
      const count = Number(raw);
      setFamilySwitchAvailable(Number.isFinite(count) && count >= 2);
    };
    updateFromStorage();
    const handler = (event) => {
      const count = Number(event?.detail?.count);
      if (!Number.isNaN(count)) {
        setFamilySwitchAvailable(count >= 2);
      }
    };
    window.addEventListener("parentPortal:familiesLoaded", handler);
    return () => {
      window.removeEventListener("parentPortal:familiesLoaded", handler);
    };
  }, [isPortal]);

  return (
    <div
      className={[
        "relative z-50 mb-4 sm:mb-6 flex items-center justify-between overflow-visible rounded-2xl p-3 sm:p-5 shadow-xl backdrop-blur-md transition supports-[backdrop-filter]:bg-white/25 max-w-5xl mx-auto",
        isLocalhost
          ? "border border-amber-300/80 bg-gradient-to-r from-amber-100/80 via-amber-200/70 to-amber-100/80 dark:border-amber-500/50 dark:from-amber-900/45 dark:via-amber-800/35 dark:to-amber-900/45"
          : "border border-white/30 bg-white/50 dark:border-white/10 dark:bg-gray-800/50 dark:supports-[backdrop-filter]:bg-gray-800/30",
      ].join(" ")}
    >
      <div
        className={[
          "pointer-events-none absolute inset-0 -z-10 rounded-2xl",
          isLocalhost
            ? "bg-gradient-to-br from-amber-100/70 via-amber-100/30 to-transparent dark:from-amber-900/40 dark:via-amber-900/15 dark:to-transparent"
            : "bg-gradient-to-br from-white/60 via-white/20 to-transparent dark:from-gray-800/70 dark:via-gray-800/30 dark:to-transparent",
        ].join(" ")}
      />
      <TitleBreadcrumb
        title="Werfklas"
        subtitle={t("parentPortal.title", { defaultValue: "Parent Portal" })}
        showEnv={true}
        variant="parent"
      />
      {/* Desktop actions */}
      <div className="hidden sm:flex items-center space-x-3">
        <RoleSwitchButton
          show={showSwitchToAdmin}
          label={t("home.switch_to_admin", { defaultValue: "Switch to Staff" })}
          onClick={() => {
            setActiveRole("admin");
            navigate(staffStartRoute, { replace: true });
          }}
        />
        <UserMenu
          onProfilePath="/portal/profile"
          backendOnline={backendOnline}
          variant="parent"
          showFamilySwitch={isPortal && familySwitchAvailable}
          onFamilySwitch={() => {
            if (typeof window !== "undefined") {
              window.location.reload();
            }
          }}
        />
      </div>

      {/* Mobile hamburger actions */}
      <div className="sm:hidden flex items-center gap-2">
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="inline-flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-sm font-medium shadow-sm ring-1 ring-gray-200 hover:bg-white dark:bg-gray-800/70 dark:text-gray-100 dark:ring-gray-700">
            <FiMenu className="h-5 w-5" />
            {t("common.menu", { defaultValue: "Menu" })}
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right z-50 rounded-xl border border-gray-200/80 bg-white/95 shadow-xl backdrop-blur-sm ring-1 ring-black/5 text-gray-900 focus:outline-none dark:border-gray-700/80 dark:bg-gray-800/95 dark:text-gray-200 dark:ring-white/10">
              <div className="py-1">
                {showSwitchToAdmin && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 dark:text-gray-200 ${active ? "bg-gray-100 dark:bg-gray-700" : ""}`}
                        onClick={() => {
                          setActiveRole("admin");
                          navigate(staffStartRoute, { replace: true });
                        }}
                      >
                        <FiArrowRight className="h-4 w-4" />
                        {t("home.switch_to_admin", { defaultValue: "Switch to Staff" })}
                      </button>
                    )}
                  </Menu.Item>
                )}

                {canOpenProfile && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 dark:text-gray-200 ${
                          active ? "bg-gray-100 dark:bg-gray-700" : ""
                        }`}
                        onClick={() => navigate("/portal/profile")}
                      >
                        <FiUser className="h-4 w-4" />
                        {t("navbar.profile", { defaultValue: "Profile" })}
                      </button>
                    )}
                  </Menu.Item>
                )}
                
                {familySwitchAvailable && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-emerald-700 dark:text-emerald-300 ${active ? "bg-gray-100 dark:bg-gray-700" : ""}`}
                        onClick={() => {
                          if (typeof window !== "undefined") {
                            window.location.reload();
                          }
                        }}
                      >
                        <FiRefreshCcw className="h-4 w-4" />
                        {t("parentPortal.actions.switch_family", { defaultValue: "Switch family" })}
                      </button>
                    )}
                  </Menu.Item>
                )}

                {/* Divider */}
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`flex w-full items-center gap-2 px-4 py-2 text-left text-red-600 dark:text-red-400 ${active ? "bg-gray-100 dark:bg-gray-700" : ""}`}
                      onClick={() => {
                        if (typeof window !== "undefined") {
                          // mirror UserMenu behavior
                          const kc = (typeof keycloak !== "undefined" && keycloak) || null;
                          kc?.logout?.({ redirectUri: config.frontendUrl });
                        }
                      }}
                    >
                      <FiLogOut className="h-4 w-4" />
                      {t("navbar.logout", { defaultValue: "Log out" })}
                    </button>
                    
                  )}
                </Menu.Item>

              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}
